import { useAuth } from '@shared/common/providers/AuthProvider';
import { concatCurrencyToAmount } from '@shared/helpers/web3/formatters';

import { PriceDetails } from '@ui/uikit/components/common/PriceDetails';
import { formatReward } from '@ui/uikit/utils';

import { useLastRoundInfo } from '../../../model/web3Raffle/api/useLastRoundInfo';
import { useRoundTickets } from '../../../model/web3Raffle/api/useRoundTickets';
import { useTicketPrice } from '../../../model/web3Raffle/api/useTicketPrice';
import { TICKETS_NEEDED_FOR_BONUS } from '../../../model/web3Raffle/bonus';
import type { Web3RaffleSupportedToken } from '../../../model/web3Raffle/supportedTokens';

type Props = {
  tokenName: Web3RaffleSupportedToken;
  price: number;
  ticketsAmount: number;
  isLoading?: boolean;
};

export const TicketPriceDetails = ({ tokenName, price, ticketsAmount, isLoading }: Props) => {
  const { data, isPending } = useTicketPrice('USDT');
  const { data: lastRoundInfo } = useLastRoundInfo();
  const { userEvmWallet } = useAuth();

  const { data: roundTickets } = useRoundTickets(
    lastRoundInfo?.roundId,
    userEvmWallet?.attributes?.address,
  );

  const ticketPrice = `${concatCurrencyToAmount(formatReward(price), tokenName, {
    stableToDollar: false,
  })}`;

  const usd = tokenName !== 'USDT' && !isPending && data ? `(${data.formatted})` : '';

  const boughtTicketsWithoutBonus =
    (Number(roundTickets?.boughtTicketsCount) ?? 0) % TICKETS_NEEDED_FOR_BONUS;

  const bonusTicketsAmount = Math.floor(
    (ticketsAmount + boughtTicketsWithoutBonus) / TICKETS_NEEDED_FOR_BONUS,
  );

  const items = [
    {
      description: 'Ticket Price',
      value: (
        <span>
          {usd && <span className="text-base-text-tertiary">{usd}&nbsp;</span>}
          <span className="text-base-text-primary">{ticketPrice}</span>
        </span>
      ),
    },
    {
      description: 'Total Tickets',
      value: (
        <span>
          {<span className="text-base-text-primary">{ticketsAmount}</span>}
          {!!bonusTicketsAmount && (
            <span className="text-base-text-pink"> +{bonusTicketsAmount} Free</span>
          )}
        </span>
      ),
    },
    {
      description: 'Total',
      value: concatCurrencyToAmount(formatReward(ticketsAmount * price), tokenName, {
        stableToDollar: false,
      }),
      isStrong: true,
    },
  ];

  return <PriceDetails items={items} isLoading={isLoading} />;
};
